<template>
  <div class="line-container">
    <svg :width="svgWidth" :height="svgHeight">
      <line
        :x1="startPos.x"
        :y1="startPos.y"
        :x2="direction === 'horizontal' ? endPos.x - 10 : endPos.x"
        :y2="endPos.y"
        :style="{ stroke: 'var(--diagram-lane-divider)' }"
        stroke-linejoin="round"
        stroke-width="1"
        :marker-end="`url(#arrowhead-${direction})`"
      />
    </svg>
  </div>
</template>

<script>
export default {
  props: {
    startElementId: String,
    endElementId: String,
    offsetParentId: String,
    refreshKey: Number,
    spacingStartElement: {
      type: Number,
      default: 0
    },
    spacingEndElement: {
      type: Number,
      default: 0
    },
    offsetStartElement: {
      type: Number,
      default: 0
    },
    offsetEndElement: {
      type: Number,
      default: 0
    },
    direction: {
      type: String,
      default: 'horizontal'
    }
  },
  data () {
    return {
      startPos: { x: 0, y: 0 },
      endPos: { x: 0, y: 0 },
      svgWidth: 0,
      svgHeight: 0
    }
  },
  mounted () {
    this.updateLinePositions()
    window.addEventListener('resize', this.updateLinePositions, { passive: true })
    document.addEventListener('scroll', this.updateLinePositionsDirect, { capture: true, passive: true })
    this.$nextTick(() => {
      this.cacheElements()
      this.updateLinePositions()
    })
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.updateLinePositions)
    document.removeEventListener('scroll', this.updateLinePositionsDirect, { capture: true })
  },
  methods: {
    cacheElements () {
      this._startEl = document.getElementById(this.startElementId)
      this._endEl = document.getElementById(this.endElementId)
      this._parentEl = this.offsetParentId ? document.getElementById(this.offsetParentId) : this._startEl?.offsetParent
      this._svgEl = this.$el?.querySelector('svg')
      this._lineEl = this.$el?.querySelector('line')
    },
    updateLinePositionsDirect () {
      if (!this._startEl?.isConnected || !this._endEl?.isConnected || !this._parentEl?.isConnected) {
        this.cacheElements()
      }

      const startEl = this._startEl
      const endEl = this._endEl
      const parentEl = this._parentEl

      if (!startEl || !endEl || !parentEl) {
        return
      }

      const startRect = startEl.getBoundingClientRect()
      const endRect = endEl.getBoundingClientRect()
      const vh = window.innerHeight
      const startVisible = startRect.bottom > -100 && startRect.top < vh + 100
      const endVisible = endRect.bottom > -100 && endRect.top < vh + 100

      const svgEl = this._svgEl
      const lineEl = this._lineEl
      if (!svgEl || !lineEl) {
        return
      }

      if (!startVisible || !endVisible) {
        svgEl.style.visibility = 'hidden'
        return
      }
      svgEl.style.visibility = ''

      const parentRect = parentEl.getBoundingClientRect()

      let x1, y1, x2, y2

      if (this.direction === 'vertical') {
        x1 = startRect.left - parentRect.left + startRect.width / 2 + this.offsetStartElement
        y1 = startRect.top - parentRect.top + startRect.height + this.spacingStartElement
        x2 = endRect.left - parentRect.left + endRect.width / 2 + this.offsetEndElement
        y2 = endRect.top - parentRect.top - this.spacingEndElement
      } else {
        x1 = startRect.right - parentRect.left - this.spacingStartElement
        y1 = startRect.top - parentRect.top + startRect.height / 2 + this.offsetStartElement
        x2 = endRect.left - parentRect.left + this.spacingEndElement
        y2 = endRect.top - parentRect.top + endRect.height / 2 + this.offsetEndElement
      }

      const w = parentEl.offsetWidth
      const h = parentEl.offsetHeight
      const finalX2 = this.direction === 'horizontal' ? x2 - 10 : x2

      svgEl.setAttribute('width', w)
      svgEl.setAttribute('height', h)
      lineEl.setAttribute('x1', x1)
      lineEl.setAttribute('y1', y1)
      lineEl.setAttribute('x2', finalX2)
      lineEl.setAttribute('y2', y2)
    },
    updateLinePositions () {
      const startElement = document.getElementById(this.startElementId)
      const endElement = document.getElementById(this.endElementId)
      const startOffsetParent = this.offsetParentId ? document.getElementById(this.offsetParentId) : startElement?.offsetParent
      const endOffsetParent = endElement?.offsetParent

      if (!startElement || !endElement || !startOffsetParent || !endOffsetParent) {
        this.svgWidth = 0
        this.svgHeight = 0
        return
      }

      const startRect = startElement.getBoundingClientRect()
      const endRect = endElement.getBoundingClientRect()
      const vh = window.innerHeight
      const startVisible = startRect.bottom > -100 && startRect.top < vh + 100
      const endVisible = endRect.bottom > -100 && endRect.top < vh + 100

      if (!startVisible || !endVisible) {
        this.svgWidth = 0
        this.svgHeight = 0
        return
      }

      if (this.direction === 'vertical') {
        this.startPos = {
          x: startRect.left - startOffsetParent.getBoundingClientRect().left + startRect.width / 2 + this.offsetStartElement,
          y: startRect.top - startOffsetParent.getBoundingClientRect().top + startRect.height + this.spacingStartElement
        }

        this.endPos = {
          x: endRect.left - startOffsetParent.getBoundingClientRect().left + endRect.width / 2 + this.offsetEndElement,
          y: endRect.top - startOffsetParent.getBoundingClientRect().top - this.spacingEndElement
        }
      } else {
        this.startPos = {
          x: startRect.right - startOffsetParent.getBoundingClientRect().left - this.spacingStartElement,
          y: startRect.top - startOffsetParent.getBoundingClientRect().top + startRect.height / 2 + this.offsetStartElement
        }

        this.endPos = {
          x: endRect.left - startOffsetParent.getBoundingClientRect().left + this.spacingEndElement,
          y: endRect.top - startOffsetParent.getBoundingClientRect().top + endRect.height / 2 + this.offsetEndElement
        }
      }

      this.svgWidth = startOffsetParent.offsetWidth
      this.svgHeight = startOffsetParent.offsetHeight
    }
  },
  watch: {
    refreshKey () {
      this.$nextTick(() => {
        this.updateLinePositions()
      })
    }
  }
}
</script>

<style scoped>
.line-container {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 0;
}
</style>
